import React from "react";

/**
 * Animated illustration shown while waiting for the buyer to confirm the
 * payment in the MB WAY mobile app. Pure SVG + scoped CSS (no raster asset):
 * a phone with the MB WAY logo, a finger tapping it, and pulsing ripple rings.
 * Brand colors are fixed (MB WAY red / blue), not the producer theme.
 */
const WaitingAnimation = ({ size = 150, alt = "" }) => {
  return (
    <div className="mbw-anim" role="img" aria-label={alt} style={{ width: size, height: (size * 11) / 10, lineHeight: 0 }}>
      <style>
        {`
          .mbw-anim svg { width: 100%; height: 100%; display: block; overflow: visible; }
          .mbw-phone { transform-box: fill-box; transform-origin: center; animation: mbw-bob 3.2s ease-in-out infinite; }
          .mbw-hand { transform-box: fill-box; transform-origin: 50% 0%; animation: mbw-tap 1.8s cubic-bezier(0.45, 0, 0.55, 1) infinite; }
          .mbw-ring { transform-box: fill-box; transform-origin: center; opacity: 0; animation: mbw-ripple 1.8s ease-out infinite; }
          .mbw-ring--2 { animation-delay: 0.6s; }
          .mbw-glow { animation: mbw-glow 1.8s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }

          @keyframes mbw-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
          @keyframes mbw-tap {
            0%, 50%, 100% { transform: translateY(0); }
            70%, 80% { transform: translateY(-6px); }
          }
          @keyframes mbw-ripple {
            0% { transform: scale(0.35); opacity: 0; }
            18% { opacity: 0.55; }
            100% { transform: scale(1.7); opacity: 0; }
          }
          @keyframes mbw-glow { 0%, 100% { opacity: 0.25; } 60% { opacity: 0.6; } }

          @media (prefers-reduced-motion: reduce) {
            .mbw-phone, .mbw-hand, .mbw-ring, .mbw-glow { animation: none; }
            .mbw-ring { opacity: 0; }
          }
        `}
      </style>

      <svg viewBox="0 0 200 220" xmlns="http://www.w3.org/2000/svg">
        {/* soft ground shadow */}
        <ellipse cx="100" cy="206" rx="52" ry="9" fill="#0f1f47" opacity="0.10" />

        <g className="mbw-phone">
          {/* phone body */}
          <rect x="55" y="22" width="90" height="160" rx="16" fill="#1f3a6e" />
          <rect x="55" y="22" width="90" height="160" rx="16" fill="url(#mbw-sheen)" opacity="0.5" />
          {/* screen */}
          <rect x="62" y="34" width="76" height="136" rx="8" fill="#ffffff" />
          {/* speaker + home indicator */}
          <rect x="91" y="27" width="18" height="3" rx="1.5" fill="#3a558c" />
          <rect x="88" y="173" width="24" height="4" rx="2" fill="#3a558c" />

          {/* MB WAY logo on screen */}
          <g>
            <rect x="74" y="52" width="30" height="22" rx="5" fill="none" stroke="#E2001A" strokeWidth="2.6" />
            <text x="89" y="68" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="12" fontWeight="700" fill="#E2001A">
              MB
            </text>
            <text x="107" y="68" fontFamily="Arial, Helvetica, sans-serif" fontSize="10" fontWeight="700" fill="#58595B">
              WAY
            </text>
          </g>

          {/* glow behind tap target */}
          <circle className="mbw-glow" cx="100" cy="120" r="20" fill="#E2001A" opacity="0.3" />

          {/* ripple rings emitted on tap */}
          <circle className="mbw-ring" cx="100" cy="120" r="16" fill="none" stroke="#E2001A" strokeWidth="2.4" />
          <circle className="mbw-ring mbw-ring--2" cx="100" cy="120" r="16" fill="none" stroke="#E2001A" strokeWidth="2.4" />

          {/* tap target button */}
          <circle cx="100" cy="120" r="11" fill="#E2001A" />
          <circle cx="100" cy="120" r="11" fill="url(#mbw-btn)" opacity="0.35" />
        </g>

        {/* tapping hand — extended index finger (left), folded fingers (right) */}
        <g className="mbw-hand">
          {/* index finger: left-most, the only extended one.
              Tip rests 6px below the tap dot (y=120); the -6px tap peak
              lands the tip exactly on the ripple center. */}
          <rect x="92.5" y="116" width="15" height="60" rx="7.5" fill="#E9BC8E" stroke="#D89A60" strokeWidth="1.5" />
          <line x1="100" y1="138" x2="100" y2="158" stroke="#D89A60" strokeWidth="1.1" opacity="0.4" />
          {/* folded fingers as humps to the right of the index */}
          <rect x="108" y="134" width="11" height="22" rx="5.5" fill="#E2AE7E" stroke="#D89A60" strokeWidth="1.2" />
          <rect x="117" y="137" width="11" height="20" rx="5.5" fill="#DCA876" stroke="#D89A60" strokeWidth="1.2" />
          <rect x="126" y="141" width="10" height="17" rx="5" fill="#D6A06C" stroke="#D89A60" strokeWidth="1.2" />
          {/* fist / palm (covers the finger bases) */}
          <rect x="86" y="150" width="52" height="40" rx="15" fill="#E9BC8E" stroke="#D89A60" strokeWidth="1.5" />
          {/* thumb on the left side */}
          <ellipse cx="84" cy="162" rx="9" ry="13" fill="#E9BC8E" stroke="#D89A60" strokeWidth="1.5" transform="rotate(-22 84 162)" />
          <path d="M96 159 h30" stroke="#D89A60" strokeWidth="1.1" fill="none" opacity="0.45" />
        </g>

        <defs>
          <linearGradient id="mbw-sheen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.18" />
            <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="1" stopColor="#000000" stopOpacity="0.12" />
          </linearGradient>
          <radialGradient id="mbw-btn" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
};

export default WaitingAnimation;
